import { useState, useMemo } from 'react';
import { Plus, Pencil, CheckCircle2, RotateCcw, Trash2 } from 'lucide-react';
import KpiCard from '../components/KpiCard.jsx';
import Modal from '../components/Modal.jsx';
import FormField from '../components/FormField.jsx';
import Button from '../components/Button.jsx';
import ClientInvoiceFormModal from '../components/invoices/ClientInvoiceFormModal.jsx';
import {
  useClientInvoices,
  useDeleteClientInvoice,
  useMarkClientInvoicePaid,
  useUnmarkClientInvoicePaid,
} from '../hooks/useClientInvoices.js';
import { formatCurrency, formatDate } from '../lib/format.js';

// ── Date helpers (mêmes conventions que UpcomingPage — parsing local pour éviter le -1j UTC) ──

function toLocalISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const shortDateFmt = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short' });
const monthYearFmt = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' });

function formatShortDate(isoDate) {
  const [y, mo, d] = isoDate.split('-').map(Number);
  return shortDateFmt.format(new Date(y, mo - 1, d));
}

function formatMonthYear(yyyymm) {
  const [y, mo] = yyyymm.split('-').map(Number);
  const label = monthYearFmt.format(new Date(y, mo - 1, 1));
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function pluralize(count, word) {
  return `${count} ${word}${count > 1 ? 's' : ''}`;
}

// ── Filtre segmenté ────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'open', label: 'Ouvertes' },
  { value: 'paid', label: 'Payées' },
  { value: 'all', label: 'Toutes' },
];

function StatusToggle({ value, onChange }) {
  return (
    <div
      className="flex rounded-lg border border-slate-200 overflow-hidden text-xs w-fit"
      role="group"
      aria-label="Filtre par statut"
    >
      {STATUS_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          aria-pressed={value === opt.value}
          className={`px-3 py-1.5 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 ${
            value === opt.value
              ? 'bg-slate-800 text-white'
              : 'bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Séparateur de mois ────────────────────────────────────────────────────────

function MonthSeparator({ label }) {
  return (
    <div className="flex items-center gap-3 py-3 px-3" aria-hidden>
      <div className="h-px flex-1 bg-slate-200" />
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
        {label}
      </span>
      <div className="h-px flex-1 bg-slate-200" />
    </div>
  );
}

// ── Dialogue "Marquer comme encaissée" ────────────────────────────────────────

function MarkPaidDialog({ invoice, onClose }) {
  const [paidAt, setPaidAt] = useState(() => toLocalISODate(new Date()));
  const markPaid = useMarkClientInvoicePaid();

  async function handleConfirm() {
    await markPaid.mutateAsync({ id: invoice.id, paid_at: paidAt });
    onClose();
  }

  return (
    <Modal open onClose={onClose} title="Marquer comme encaissée" size="sm">
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Facture <strong className="text-slate-800">{invoice.client_name}</strong>
          {' — '}
          {formatCurrency(invoice.amount_cents)}
        </p>

        <FormField label="Date d'encaissement" htmlFor="mark-paid-date" required>
          <input
            id="mark-paid-date"
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 transition-colors"
          />
        </FormField>

        {markPaid.error && (
          <p className="text-sm text-red-600" role="alert">{markPaid.error.message}</p>
        )}

        <div className="flex gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose} disabled={markPaid.isPending} className="flex-1">
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            loading={markPaid.isPending}
            disabled={markPaid.isPending || !paidAt}
            className="flex-1"
          >
            Confirmer
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Ligne facture ──────────────────────────────────────────────────────────────

function StatusBadge({ children, className }) {
  return (
    <span className={`shrink-0 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${className}`}>
      {children}
    </span>
  );
}

function InvoiceRow({ invoice, todayISO, onEdit, onMarkPaid, onUnmarkPaid, onDelete }) {
  const isPaid = invoice.status === 'paid';
  const isOverdue = !isPaid && invoice.due_on < todayISO;

  return (
    <div className="flex items-start gap-2 px-3 py-2.5 hover:bg-slate-50 border-b border-slate-100 last:border-0">
      {/* Date d'échéance */}
      <span className="text-slate-500 text-[11px] tabular-nums w-[40px] shrink-0 pt-0.5 leading-tight">
        {formatShortDate(invoice.due_on)}
      </span>

      {/* Client + description */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="text-sm font-medium text-slate-800 truncate">{invoice.client_name}</p>
          {isOverdue && <StatusBadge className="bg-red-100 text-red-700">En retard</StatusBadge>}
          {isPaid && <StatusBadge className="bg-emerald-50 text-emerald-700">Payée</StatusBadge>}
        </div>
        {invoice.label && <p className="text-xs text-slate-500 truncate">{invoice.label}</p>}
        {isPaid && invoice.paid_at && (
          <p className="text-[11px] text-emerald-600 mt-0.5">Encaissée le {formatDate(invoice.paid_at)}</p>
        )}
      </div>

      {/* Montant */}
      <span
        className={`text-sm font-semibold tabular-nums shrink-0 ${isPaid ? 'text-slate-400' : 'text-green-600'}`}
      >
        +{formatCurrency(invoice.amount_cents)}
      </span>

      {/* Actions */}
      <div className="flex items-center gap-0.5 shrink-0">
        <button
          type="button"
          onClick={() => onEdit(invoice)}
          aria-label="Modifier la facture"
          title="Modifier"
          className="p-1.5 rounded text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
        >
          <Pencil size={14} aria-hidden />
        </button>

        {!isPaid && (
          <button
            type="button"
            onClick={() => onMarkPaid(invoice)}
            aria-label="Marquer comme encaissée"
            title="Marquer comme encaissée"
            className="p-1.5 rounded text-slate-300 hover:text-emerald-600 hover:bg-emerald-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
          >
            <CheckCircle2 size={14} aria-hidden />
          </button>
        )}

        {isPaid && (
          <button
            type="button"
            onClick={() => onUnmarkPaid(invoice)}
            aria-label="Annuler l'encaissement"
            title="Annuler l'encaissement"
            className="p-1.5 rounded text-slate-300 hover:text-orange-500 hover:bg-orange-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
          >
            <RotateCcw size={14} aria-hidden />
          </button>
        )}

        {!isPaid && (
          <button
            type="button"
            onClick={() => onDelete(invoice)}
            aria-label="Supprimer la facture"
            title="Supprimer"
            className="p-1.5 rounded text-slate-300 hover:text-red-600 hover:bg-red-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
          >
            <Trash2 size={14} aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Skeleton ───────────────────────────────────────────────────────────────────

function RowSkeleton() {
  return (
    <div className="flex items-center gap-2 px-3 py-2.5 animate-pulse">
      <div className="w-[40px] h-3.5 rounded bg-slate-200 shrink-0" />
      <div className="flex-1 h-3.5 rounded bg-slate-200 min-w-0" />
      <div className="w-[80px] h-3.5 rounded bg-slate-200 shrink-0" />
      <div className="w-[60px] h-3.5 rounded bg-slate-100 shrink-0" />
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ClientInvoicesPage() {
  const [statusFilter, setStatusFilter] = useState('open');
  const [formModal, setFormModal] = useState(null); // null | { invoice: object|null }
  const [markPaidTarget, setMarkPaidTarget] = useState(null); // null | invoice

  // Fetch unique (pas de filtre serveur) — le statut affiché et les KPIs sont
  // dérivés côté client pour éviter un refetch à chaque changement de filtre
  // (dataset restreint, YAGNI sur la pagination/recherche).
  const { data, isLoading, error, refetch } = useClientInvoices();

  const deleteMut = useDeleteClientInvoice();
  const unmarkMut = useUnmarkClientInvoicePaid();

  const allInvoices = Array.isArray(data) ? data : (data?.items ?? data?.data ?? []);

  const todayISO = toLocalISODate(new Date());

  const openInvoices = useMemo(
    () => allInvoices.filter((i) => i.status === 'open'),
    [allInvoices]
  );
  const overdueInvoices = useMemo(
    () => openInvoices.filter((i) => i.due_on < todayISO),
    [openInvoices, todayISO]
  );

  const pendingCount = openInvoices.length;
  const pendingTotal = openInvoices.reduce((sum, i) => sum + i.amount_cents, 0);
  const overdueCount = overdueInvoices.length;
  const overdueTotal = overdueInvoices.reduce((sum, i) => sum + i.amount_cents, 0);

  const filteredInvoices = useMemo(() => {
    if (statusFilter === 'open') return allInvoices.filter((i) => i.status === 'open');
    if (statusFilter === 'paid') return allInvoices.filter((i) => i.status === 'paid');
    return allInvoices;
  }, [allInvoices, statusFilter]);

  const groups = useMemo(() => {
    if (!filteredInvoices.length) return [];
    const map = new Map();
    for (const inv of filteredInvoices) {
      const key = inv.due_on.slice(0, 7);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(inv);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, items]) => ({
        month,
        items: [...items].sort((a, b) => a.due_on.localeCompare(b.due_on)),
      }));
  }, [filteredInvoices]);

  function openCreate() {
    setFormModal({ invoice: null });
  }

  function openEdit(invoice) {
    setFormModal({ invoice });
  }

  function closeForm() {
    setFormModal(null);
  }

  function closeMarkPaid() {
    setMarkPaidTarget(null);
  }

  async function handleUnmarkPaid(invoice) {
    await unmarkMut.mutateAsync(invoice.id);
  }

  async function handleDelete(invoice) {
    if (!window.confirm(`Supprimer la facture de ${invoice.client_name} ?`)) return;
    await deleteMut.mutateAsync(invoice.id);
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4 pb-24 lg:pb-6">
      {/* ── Header ── */}
      <div className="flex items-center gap-3">
        <h1 className="flex-1 text-xl font-bold text-slate-900">Factures émises</h1>
        <button
          type="button"
          onClick={openCreate}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
        >
          <Plus size={15} aria-hidden /> Nouvelle
        </button>
      </div>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-2 gap-3">
        <KpiCard
          label="En attente"
          value={formatCurrency(pendingTotal)}
          sub={pluralize(pendingCount, 'facture')}
          loading={isLoading}
        />
        <KpiCard
          label="En retard"
          value={formatCurrency(overdueTotal)}
          sub={pluralize(overdueCount, 'facture')}
          accentClass="text-red-600"
          loading={isLoading}
        />
      </div>

      {/* ── Filtre statut ── */}
      <StatusToggle value={statusFilter} onChange={setStatusFilter} />

      {/* ── Liste ── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="invoice-list">
        {isLoading && (
          <div>
            <RowSkeleton />
            <RowSkeleton />
            <RowSkeleton />
          </div>
        )}

        {error && !isLoading && (
          <div className="px-4 py-8 text-center space-y-3">
            <p className="text-sm text-red-600">Impossible de charger les factures.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="text-sm font-medium text-slate-700 underline underline-offset-2 hover:text-slate-900"
            >
              Réessayer
            </button>
          </div>
        )}

        {!isLoading && !error && groups.length === 0 && (
          <div className="px-4 py-10 text-center">
            <p className="text-sm text-slate-500">Aucune facture à afficher.</p>
          </div>
        )}

        {!isLoading && !error && groups.length > 0 && (
          <div>
            {groups.map(({ month, items }) => (
              <div key={month}>
                <MonthSeparator label={formatMonthYear(month)} />
                {items.map((invoice) => (
                  <InvoiceRow
                    key={invoice.id}
                    invoice={invoice}
                    todayISO={todayISO}
                    onEdit={openEdit}
                    onMarkPaid={setMarkPaidTarget}
                    onUnmarkPaid={handleUnmarkPaid}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {formModal && (
        <ClientInvoiceFormModal open onClose={closeForm} invoice={formModal.invoice} />
      )}
      {markPaidTarget && (
        <MarkPaidDialog invoice={markPaidTarget} onClose={closeMarkPaid} />
      )}
    </div>
  );
}
